<template>
    <van-popup v-model="visible" class="guided-camera" position="bottom" :overlay="false" @closed="releaseCamera">
        <div class="camera-header">
            <button class="icon-button" type="button" aria-label="关闭拍照" @click="close">&#215;</button>
            <span>拍摄个人照片</span>
            <button class="text-button" type="button" @click="switchCamera">切换</button>
        </div>

        <div class="camera-stage">
            <video ref="video" autoplay muted playsinline class="camera-preview" @loadedmetadata="onVideoReady"></video>
            <div class="camera-instruction">请将人脸置于轮廓内，并保持静止</div>
            <div class="face-mask" :class="status.level">
                <span class="eye-line"></span>
            </div>
            <div class="face-checks" :class="status.level">
                <span><i>✓</i>保持光线充足</span>
                <span><i>✓</i>请面对屏幕</span>
                <span><i>✓</i>请保持无表情</span>
            </div>
            <div class="camera-hint" :class="status.level">{{ status.message }}</div>
            <van-loading v-if="isStarting" class="camera-loading" color="#ffffff" vertical>正在打开相机</van-loading>
        </div>

        <div class="camera-actions">
            <button type="button" class="gallery-button" @click="openPicker">从相册选择</button>
            <button type="button" class="shutter" :disabled="!canCapture" aria-label="拍摄个人照片" @click="capture">
                <span></span>
            </button>
            <span class="action-spacer"></span>
        </div>

        <input ref="picker" class="hidden-picker" type="file" accept="image/*" @change="selectFromGallery">
    </van-popup>
</template>

<script>
import { Toast } from 'vant';
import { createFaceDetector, evaluateFaceDetections, getFaceStatus } from '@/utils/faceDetection';

export default {
    name: 'GuidedFaceCamera',
    data() {
        return {
            visible: false,
            stream: null,
            detector: null,
            facingMode: 'user',
            statusName: 'loading',
            isStarting: false,
            isCameraReady: false,
            isDetecting: false,
            lastDetectionAt: 0,
            animationFrame: null,
            cameraSession: 0,
        };
    },
    computed: {
        status() {
            return getFaceStatus(this.statusName);
        },
        canCapture() {
            return this.isCameraReady && (this.statusName === 'ready' || this.statusName === 'unavailable');
        },
    },
    beforeDestroy() {
        this.releaseCamera();
    },
    methods: {
        async open() {
            const session = ++this.cameraSession;
            this.visible = true;
            this.isStarting = true;
            this.statusName = 'loading';
            this.isCameraReady = false;

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.statusName = 'unavailable';
                this.isStarting = false;
                return;
            }

            try {
                this.stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: this.facingMode },
                        width: { ideal: 1280 },
                        height: { ideal: 1280 },
                    },
                    audio: false,
                });
            } catch (error) {
                if (session !== this.cameraSession) {
                    return;
                }
                this.statusName = 'unavailable';
                this.isStarting = false;
                Toast('无法打开相机，可从相册选择照片');
                return;
            }

            await this.$nextTick();
            if (session !== this.cameraSession || !this.visible || !this.$refs.video) {
                this.releaseCamera();
                return;
            }

            this.$refs.video.srcObject = this.stream;
            try {
                const detector = await createFaceDetector('VIDEO');
                if (session !== this.cameraSession || !this.visible) {
                    detector.close();
                    return;
                }
                this.detector = detector;
                this.startDetectionIfReady();
            } catch (error) {
                if (session !== this.cameraSession) {
                    return;
                }
                // Camera capture remains available when face guidance cannot load.
                this.statusName = 'unavailable';
                Toast('人脸识别不可用，可直接拍摄或从相册选择照片');
            } finally {
                this.isStarting = false;
            }
        },
        onVideoReady() {
            this.isCameraReady = true;
            this.startDetectionIfReady();
        },
        startDetectionIfReady() {
            const video = this.$refs.video;
            if (this.animationFrame || !this.visible || !this.detector || !video || video.readyState < 2) {
                return;
            }
            this.detectLoop();
        },
        detectLoop(timestamp = performance.now()) {
            if (!this.visible || !this.$refs.video) {
                return;
            }

            this.animationFrame = requestAnimationFrame((nextTimestamp) => this.detectLoop(nextTimestamp));
            if (this.isDetecting || timestamp - this.lastDetectionAt < 200 || !this.detector) {
                return;
            }

            const video = this.$refs.video;
            if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
                return;
            }

            this.isDetecting = true;
            this.lastDetectionAt = timestamp;
            try {
                const result = this.detector.detectForVideo(video, timestamp);
                this.statusName = evaluateFaceDetections(result.detections, video.videoWidth, video.videoHeight);
            } catch (error) {
                this.statusName = 'unavailable';
            } finally {
                this.isDetecting = false;
            }
        },
        capture() {
            if (!this.canCapture) {
                return;
            }

            const video = this.$refs.video;
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                if (!blob) {
                    Toast('拍摄失败，请重试');
                    return;
                }
                const file = new File([blob], `personal-photo-${Date.now()}.jpg`, {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                });
                this.$emit('capture', file);
                this.close();
            }, 'image/jpeg', 0.92);
        },
        openPicker() {
            this.$refs.picker.click();
        },
        selectFromGallery(event) {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) {
                return;
            }
            this.$emit('select', file);
            this.close();
        },
        async switchCamera() {
            this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
            this.releaseCamera();
            await this.open();
        },
        close() {
            this.visible = false;
        },
        releaseStream() {
            if (this.stream) {
                this.stream.getTracks().forEach((track) => track.stop());
                this.stream = null;
            }
            if (this.$refs.video) {
                this.$refs.video.srcObject = null;
            }
        },
        releaseCamera() {
            this.cameraSession += 1;
            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
                this.animationFrame = null;
            }
            this.releaseStream();
            if (this.detector) {
                this.detector.close();
                this.detector = null;
            }
            this.isDetecting = false;
            this.isCameraReady = false;
            this.lastDetectionAt = 0;
        },
    },
};
</script>

<style scoped>
.guided-camera {
    height: 100%;
    width: 100%;
    background: #101513;
    color: #fff;
}

.camera-header,
.camera-actions {
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 16px 20px;
}

.camera-header {
    font-size: 16px;
    min-height: 52px;
    box-sizing: border-box;
}

.icon-button,
.text-button,
.gallery-button {
    background: transparent;
    border: 0;
    color: inherit;
    font: inherit;
    padding: 8px;
}

.icon-button {
    font-size: 34px;
    line-height: 22px;
}

.text-button,
.gallery-button {
    font-size: 14px;
}

.camera-stage {
    background: #000;
    height: calc(100% - 152px);
    min-height: 340px;
    overflow: hidden;
    position: relative;
}

.camera-preview {
    height: 100%;
    object-fit: cover;
    transform: scaleX(-1);
    width: 100%;
}

.face-mask {
    background: transparent;
    border: 2px dashed rgba(255, 255, 255, 0.94);
    border-radius: 48% 48% 45% 45% / 39% 39% 57% 57%;
    box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.48);
    height: min(96vw, 360px);
    left: 50%;
    overflow: visible;
    position: absolute;
    top: 49%;
    transform: translate(-50%, -50%);
    width: min(70.67vw, 265px);
    z-index: 1;
}

.face-mask::before,
.face-mask::after {
    background: transparent;
    border: 2px dashed rgba(255, 255, 255, 0.94);
    content: '';
    height: 39px;
    position: absolute;
    top: 43%;
    width: 17px;
}

.face-mask::before {
    border-radius: 20px 0 0 20px;
    border-right: 0;
    left: -12px;
}

.face-mask::after {
    border-left: 0;
    border-radius: 0 20px 20px 0;
    right: -12px;
}

.eye-line {
    background: rgba(255, 255, 255, 0.76);
    box-shadow: 0 0 6px rgba(255, 255, 255, 0.28);
    height: 2px;
    left: 27px;
    position: absolute;
    right: 27px;
    top: 47%;
}

.camera-instruction {
    left: 20px;
    position: absolute;
    right: 20px;
    text-align: center;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
    top: 22px;
    z-index: 2;
}

.face-checks {
    bottom: 74px;
    display: flex;
    justify-content: space-around;
    left: 8px;
    position: absolute;
    right: 8px;
    text-align: center;
    z-index: 2;
}

.face-checks span {
    font-size: 11px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.48);
    width: 31%;
}

.face-checks i {
    align-items: center;
    background: rgba(168, 177, 173, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.46);
    border-radius: 50%;
    display: flex;
    font-size: 13px;
    font-style: normal;
    height: 20px;
    justify-content: center;
    margin: 0 auto 5px;
    width: 20px;
}

.face-checks.ready i {
    background: #10ba77;
    border-color: #73deb7;
}

.camera-hint {
    background: rgba(100, 64, 14, 0.85);
    border-radius: 18px;
    bottom: 28px;
    font-size: 14px;
    left: 50%;
    max-width: calc(100% - 40px);
    padding: 9px 14px;
    position: absolute;
    text-align: center;
    transform: translateX(-50%);
    white-space: nowrap;
    z-index: 2;
}

.camera-hint.ready {
    background: rgba(3, 103, 76, 0.9);
}

.camera-hint.loading,
.camera-hint.error {
    background: rgba(48, 55, 51, 0.9);
}

.camera-loading {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
}

.camera-actions {
    min-height: 100px;
    box-sizing: border-box;
}

.gallery-button,
.action-spacer {
    min-width: 92px;
}

.shutter {
    background: #fff;
    border: 4px solid #bfc8c4;
    border-radius: 50%;
    box-shadow: inset 0 0 0 5px #fff;
    height: 64px;
    padding: 0;
    width: 64px;
}

.shutter:disabled {
    opacity: 0.38;
}

.hidden-picker {
    display: none;
}
</style>
